import { PROGRESS_WEIGHTS, type ProgressIssue, type TechnicalProgress } from '@crm/shared';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { formatDate } from '../lib/format';
import { Fiber } from './Fiber';
import { Icon } from './Icon';

const STATE: Record<TechnicalProgress['state'], string> = {
  not_accepted: 'Not accepted yet', in_progress: 'In progress', paused: 'Paused: clarification', ready: 'Quotation ready', ready_check: 'Quotation ready: to check',
};
const signed = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : '±'}${Math.abs(n)}`;

/** How the percentage is made, in one line (also the tooltip). */
export function progressHow(p: TechnicalProgress): string {
  const parts = [`accepted ${p.parts.acceptance}/${PROGRESS_WEIGHTS.acceptance}`,
    `deliverables ${p.parts.deliverables ?? '—'}/${PROGRESS_WEIGHTS.deliverables}`, `tasks ${p.parts.tasks ?? '—'}/${PROGRESS_WEIGHTS.tasks}`];
  return `Technical progress = ${parts.join(' + ')}. At most 95% until a valid Quotation Ready. Pre-Sales work only: not Deal Health, not CRM compliance.`;
}

/**
 * Pre-Sales technical progress of one assignment: the stage line (blue and moving while in progress, amber and still
 * while waiting for clarification, green once the quotation is ready, dashed while it can't be measured), then actual
 * progress, expected progress and the schedule side by side, short issue chips, "Accept and start" for the lead
 * engineer, and "View issues" with each issue's detail and corrective action.
 */
export function TechProgress({ p, lead, support = [], compact, dealId, onAccept, acceptBusy, moreActions }: {
  p: TechnicalProgress; lead: string | null; support?: string[]; compact?: boolean;
  /** The deal, for the corrective-action links. */ dealId?: string;
  /** Shown to the lead engineer (or an administrator) while the assignment isn't accepted: the existing Start action. */
  onAccept?: () => void; acceptBusy?: boolean;
  /** The assignment's other actions (dates, clarification…), shown next to Accept and start. */
  moreActions?: ReactNode;
}) {
  const reliable = !p.insufficient;
  // Blue and moving while the work goes on; not measured yet: the light sweeps the whole line (the label says so).
  const tone = p.state === 'ready' ? 'done' : p.state === 'paused' ? 'paused' : !reliable ? 'scan' : 'run';
  const engineers = [lead, ...support].filter(Boolean) as string[];
  const pace = p.pace;
  const issues = p.issues ?? [];
  const brands = dealId ? `/o/opportunity/${dealId}#quoted-brands` : null;
  const action = (i: ProgressIssue) => {
    // Accept and start is the button next to the chips (one button, not two).
    if (i.action === 'accept') return onAccept ? <span className="muted">(Accept and start, above)</span> : null;
    if ((i.action === 'quoted_brands' || i.action === 'read_costing') && brands) return <Link to={brands}>{i.action === 'read_costing' ? 'Read costing (Our Quoted Brands)' : 'Open Our Quoted Brands'} →</Link>;
    if (i.action === 'tasks' && dealId) return <Link to={`/o/opportunity/${dealId}`}>Open the deal’s tasks →</Link>;
    return null;
  };
  return (
    <div className={`tp${compact ? ' compact' : ''} tp-${p.state}${reliable ? '' : ' tp-na'}`}>
      <div className="tp-head">
        <span className="tp-label" title="Pre-Sales work only: not Deal Health, not CRM compliance">Technical progress</span>
        {reliable ? <b className="tp-pct" title={progressHow(p)}>{p.percent}%</b> : <b className="tp-pct unknown">Progress not available</b>}
        <span className="tp-state">{STATE[p.state]}</span>
        {engineers.length > 0 && <span className="tp-who" title={engineers.join(', ')}>· {engineers[0]}{engineers.length > 1 ? ` +${engineers.length - 1}` : ''}</span>}
      </div>
      <div role="progressbar" aria-label="Technical progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={reliable ? p.percent : undefined}
        aria-valuetext={reliable ? `${p.percent}%, ${STATE[p.state]}` : 'Progress not available: not enough tracking data'}>
        <Fiber lit={p.percent} tone={tone} pace={pace?.expected ?? null} />
      </div>
      <div className="tp-metrics">
        <div><span>Actual</span><b>{reliable ? `${p.percent}%` : '—'}</b></div>
        <div title="Expected by today — a schedule marker, not completion: where the work should be by now (start to deadline)"><span>Expected</span>
          <b>{pace ? `${pace.expected}%` : '—'}{pace?.variance != null && <small className={pace.variance < -10 ? 'tp-late' : 'tp-ok'}> {signed(pace.variance)}</small>}</b></div>
        <div><span>Schedule</span>
          <b className={pace?.overdue ? 'tp-late' : ''}>{p.state === 'ready' ? 'Done'
            : pace ? (pace.overdue ? `${-pace.daysRemaining} day${pace.daysRemaining === -1 ? '' : 's'} overdue` : `${pace.daysRemaining} day${pace.daysRemaining === 1 ? '' : 's'} left`) : 'No deadline'}</b>
          {pace && p.state !== 'ready' && <small className="muted">due {formatDate(pace.deadline)}</small>}</div>
      </div>
      {(issues.length > 0 || onAccept) && (
        <div className="tp-chips">
          {issues.map((i) => <span key={i.key + i.label} className={`tp-chip ${i.level}`} title={i.detail}>{i.level === 'block' ? <Icon name="pause" /> : null}{i.label}</span>)}
          {onAccept && issues.some((i) => i.key === 'not_accepted') && (
            <button type="button" className="btn small primary tp-accept" disabled={acceptBusy} onClick={onAccept}>Accept and start</button>
          )}
          {moreActions}
        </div>
      )}
      {(p.postQuotation.openRequests > 0 || p.postQuotation.toRevise > 0) && (
        <div className="small"><span className="lg-chip" title="After the quotation: tracked apart from this progress">Submittals: {[p.postQuotation.openRequests ? `${p.postQuotation.openRequests} requested` : null, p.postQuotation.toRevise ? `${p.postQuotation.toRevise} to revise` : null].filter(Boolean).join(', ')}</span></div>
      )}
      <details className="tp-details">
        <summary>{issues.length ? `View issues (${issues.length})` : 'How it is calculated'}</summary>
        {issues.length > 0 && (
          <ul className="tp-issues">{issues.map((i) => (
            <li key={i.key + i.label} className={i.level}><b>{i.label}</b> — {i.detail} {action(i)}</li>
          ))}</ul>
        )}
        {p.insufficient && <p className="small muted">Why no percentage: {p.insufficient.join('; ')}. What is known so far (acceptance and tasks: {p.percent}%) isn’t a reliable measure.</p>}
        {p.tasks.total > 0 && <p className="small muted">{p.tasks.done} of {p.tasks.total} technical tasks of the engineers done.</p>}
        <p className="small muted">{progressHow(p)} The tick on the line is the expected progress for the dates (start to the internal deadline, else the customer submission date): a schedule marker, hidden when the dates are missing or unreliable.</p>
      </details>
    </div>
  );
}
